import { useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material'
import { CirclePlay, CircleStop, Download, FileUp, History, Pencil, Plus, Trash2, TrendingDown, UserPlus } from 'lucide-react'
import { AppShell, Crumb, Flag as CountryFlag, SkeletonCards, Surface, formatDate, saveAs, tokens, useNavigate } from '@agribot/polygon-checks-ui'
import {
  downloadAssignmentTemplate, useAssignments, useCampaigns, useCreateCampaign, useImportAssignments, useRoles, useUpdateCampaign,
  type Assignment, type AssignmentImport, type CampaignRow, type OpenBlocker,
} from '../api'
import { AssignmentDialog, type AssignmentDialogMode } from '../components/AssignmentDialog'
import { DemandPanel } from '../components/DemandPanel'
import { ImportDialog } from '../components/ImportDialog'
import { DeleteDialog } from '../components/DeleteDialog'
import { CampaignReminders, CampaignSchedule, MISSING } from '../components/CampaignSchedule'
import { STATUS_LABEL } from '../rules'
import { scenarioLink } from '../scenario'

const STATUS: Record<CampaignRow['status'], { label: string; bg: string; fg: string }> = {
  draft: { label: 'Draft', bg: tokens.color.ink100, fg: tokens.color.ink600 },
  open: { label: 'Open', bg: tokens.color.brandGreen100, fg: '#1f4741' },
  closed: { label: 'Closed', bg: tokens.color.ink200, fg: tokens.color.ink700 },
}

function CampaignStatus({ status }: { status: CampaignRow['status'] }) {
  const s = STATUS[status]
  return <Box component="span" sx={{ display: 'inline-flex', height: 22, px: 2, alignItems: 'center', borderRadius: 999, backgroundColor: s.bg, color: s.fg, font: `500 12px ${tokens.font.sans}` }}>{s.label}</Box>
}

/** Screen 10, the list: every campaign with its dates, status and progress; a new one starts here. */
export function Campaigns() {
  const { data, isPending, error } = useCampaigns()
  const canEdit = useRoles().includes('admin')
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState<string | null>(null)
  return (
    <AppShell
      crumbs={[<Crumb key="a">Sourcing</Crumb>, <Crumb key="b">Campaigns</Crumb>]}
      actions={canEdit ? <Button startIcon={<Plus size={16} />} onClick={() => setCreating(true)}>New campaign</Button> : undefined}
    >
      <Box sx={{ flex: 1, overflow: 'auto', px: 8, py: 7 }}>
        <Typography variant="h1" sx={{ mb: 2 }}>Campaigns</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 6, maxWidth: 720 }}>
          Each year’s data collection: which suppliers answer for which materials, who reviews them, and when it opens and closes.
          Suppliers are emailed when it opens. Materials answered and approved last year arrive pre-filled.
        </Typography>
        {error && <Alert severity="error">{(error as Error).message}</Alert>}
        {isPending && <SkeletonCards columns="1fr" />}
        {data && (
          <Surface sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 820 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Campaign</TableCell>
                  <TableCell>Open</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Suppliers</TableCell>
                  <TableCell align="right">Materials</TableCell>
                  <TableCell align="right">Pre-filled</TableCell>
                  <TableCell>Submissions</TableCell>
                  {canEdit && <TableCell sx={{ width: 56 }}><Box component="span" sx={{ position: 'absolute', left: -9999 }}>Actions</Box></TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length === 0 && (
                  <TableRow><TableCell colSpan={canEdit ? 8 : 7} sx={{ color: 'text.secondary' }}>No campaigns yet.</TableCell></TableRow>
                )}
                {data.map((c) => (
                  <TableRow key={c.id} hover sx={{ cursor: 'pointer', '& td': { height: 56 } }} onClick={() => navigate(`/sourcing/campaigns/${c.id}`)}>
                    <TableCell><Box sx={{ fontWeight: 500, color: 'text.primary' }}>{c.name}</Box><Box sx={{ fontSize: 12, color: 'text.secondary' }}>{c.year}</Box></TableCell>
                    <TableCell>{formatDate(c.opensAt)} – {formatDate(c.closesAt)}</TableCell>
                    <TableCell><CampaignStatus status={c.status} /></TableCell>
                    <TableCell align="right">{c.assignedSuppliers ?? '—'}</TableCell>
                    <TableCell align="right">{c.assignedMaterials ?? '—'}</TableCell>
                    <TableCell align="right">{c.prefilledMaterials ?? '—'}</TableCell>
                    <TableCell>
                      {Object.entries(c.submissions ?? {}).map(([s, n]) => `${n} ${(STATUS_LABEL as Record<string, string>)[s]?.toLowerCase() ?? s}`).join(' · ') || '—'}
                    </TableCell>
                    {canEdit && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Delete campaign">
                          <IconButton aria-label={`Delete ${c.name}`} onClick={() => setDeleting(c.id)} sx={{ color: tokens.color.ink500, '&:hover': { color: tokens.color.danger } }}>
                            <Trash2 size={16} strokeWidth={1.75} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Surface>
        )}
      </Box>
      {creating && <CreateCampaign onClose={() => setCreating(false)} onCreated={(id) => navigate(`/sourcing/campaigns/${id}`)} />}
      {deleting && <DeleteDialog target={{ kind: 'campaign', id: deleting }} onClose={() => setDeleting(null)} />}
    </AppShell>
  )
}

function CreateCampaign({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const create = useCreateCampaign()
  const next = new Date().getFullYear() + 1
  const [year, setYear] = useState(String(next))
  const [name, setName] = useState(`Raw material sustainability survey ${next}`)
  const [opens, setOpens] = useState(`${next}-09-01`)
  const [closes, setCloses] = useState(`${next}-10-31`)
  const bad = !name.trim() || !/^\d{4}$/.test(year) || !opens || !closes || closes <= opens
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <Box component="form" onSubmit={(e) => {
        e.preventDefault()
        create.mutate(
          { year: Number(year), name: name.trim(), opensAt: new Date(`${opens}T00:00:00Z`).toISOString(), closesAt: new Date(`${closes}T23:59:00Z`).toISOString() },
          { onSuccess: (r) => onCreated((r as { id: string }).id) },
        )
      }}>
        <DialogTitle>New campaign</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 4, pt: '8px !important' }}>
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required InputLabelProps={{ shrink: true }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr', gap: 3 }}>
            <TextField label="Year" value={year} onChange={(e) => setYear(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField type="date" label="Opens" value={opens} onChange={(e) => setOpens(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField type="date" label="Closes" value={closes} onChange={(e) => setCloses(e.target.value)} InputLabelProps={{ shrink: true }}
              error={Boolean(opens && closes && closes <= opens)} helperText={opens && closes && closes <= opens ? 'After it opens.' : ' '} />
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>It starts as a draft: assign suppliers and reviewers, then open it. Nobody is emailed until then.</Typography>
          {create.isError && <Alert severity="error">{(create.error as Error).message}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 6, pb: 5, gap: 2 }}>
          <Button variant="text" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={bad || create.isPending}>Create</Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

/** Screen 10, one campaign: who answers what and who reviews it, added one by one or from a spreadsheet; then open and close. */
export function CampaignDetail() {
  const { campaignId = '' } = useParams()
  const { data: campaigns } = useCampaigns()
  const campaign = campaigns?.find((c) => c.id === campaignId)
  const { data: rows, isPending, error } = useAssignments(campaignId)
  const update = useUpdateCampaign(campaignId)
  const canEdit = useRoles().includes('admin')
  const [importing, setImporting] = useState(false)
  const [blockers, setBlockers] = useState<OpenBlocker[]>([])
  const [confirm, setConfirm] = useState<'open' | 'close' | null>(null)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [assigning, setAssigning] = useState<AssignmentDialogMode | null>(null)
  const [removing, setRemoving] = useState<Assignment | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const navigate = useNavigate()
  const status = campaign?.status
  const canAssign = canEdit && status !== undefined && status !== 'closed'

  const go = (to: 'open' | 'closed') => update.mutate({ status: to }, {
    onSuccess: (r) => { setBlockers(r.blockers); setConfirm(null) },
  })

  return (
    <AppShell
      crumbs={[<Crumb key="a">Sourcing</Crumb>, <Crumb key="b" to="/sourcing/campaigns">Campaigns</Crumb>, <Crumb key="c">{campaign?.name ?? '…'}</Crumb>]}
      actions={campaign ? (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<TrendingDown size={16} />} onClick={() => navigate(scenarioLink(campaign.id))}>Run a scenario</Button>
          {canEdit && <>
          <Button variant="outlined" startIcon={<Download size={16} />} onClick={() => {
            setDownloadError(null)
            downloadAssignmentTemplate(campaignId).then((b) => saveAs(b, 'campaign-assignments-template.csv')).catch((e: Error) => setDownloadError(e.message))
          }}>Template</Button>
          {status !== 'closed' && <Button variant="outlined" startIcon={<UserPlus size={16} />} onClick={() => setAssigning({ kind: 'add', inCampaign: rows?.map((r) => r.supplierId) ?? [] })}>Add suppliers</Button>}
          {status !== 'closed' && <Button variant="outlined" startIcon={<FileUp size={16} />} onClick={() => setImporting(true)}>Import assignments</Button>}
          {status === 'draft' && <Button startIcon={<CirclePlay size={16} />} onClick={() => setConfirm('open')}>Open campaign</Button>}
          {status === 'open' && <Button variant="outlined" color="error" startIcon={<CircleStop size={16} />} onClick={() => setConfirm('close')}>Close campaign</Button>}
          <Button variant="text" color="error" startIcon={<Trash2 size={16} />} onClick={() => setDeleting(true)}>Delete</Button>
          </>}
        </Box>
      ) : undefined}
    >
      <Box sx={{ flex: 1, overflow: 'auto', px: 8, py: 7 }}>
        {campaign && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="h1">{campaign.name}</Typography>
            <CampaignStatus status={campaign.status} />
          </Box>
        )}
        {campaign && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 6 }}>
            {formatDate(campaign.opensAt)} – {formatDate(campaign.closesAt)} · {campaign.assignedSuppliers ?? 0} suppliers · {campaign.assignedMaterials ?? 0} materials
            {campaign.prefilledMaterials ? ` · ${campaign.prefilledMaterials} pre-filled from last year` : ''}
          </Typography>
        )}
        {downloadError && <Alert severity="error" sx={{ mb: 4 }}>{downloadError}</Alert>}
        {notice && <Alert severity="success" sx={{ mb: 4 }} onClose={() => setNotice(null)}>{notice}</Alert>}
        {update.isError && <Alert severity="error" sx={{ mb: 4 }}>{(update.error as Error).message}</Alert>}
        {blockers.length > 0 && (
          <Alert severity="warning" sx={{ mb: 5, '& .MuiAlert-message': { width: '100%' } }} onClose={() => setBlockers([])}>
            <Box sx={{ fontWeight: 600, mb: 1 }}>Not opened: {blockers.length} supplier{blockers.length === 1 ? '' : 's'} not ready.</Box>
            <Box component="ul" sx={{ m: 0, pl: 4 }}>
              {blockers.map((b) => <li key={b.supplierId}>{b.supplier}: {b.missing.map((m) => MISSING[m as keyof typeof MISSING] ?? m).join(', ')}</li>)}
            </Box>
          </Alert>
        )}
        {campaign && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(0, 1fr)' }, gap: 5, mb: 6, alignItems: 'start' }}>
            <CampaignSchedule campaign={campaign} canEdit={canEdit} />
            <CampaignReminders campaign={campaign} canEdit={canEdit} />
          </Box>
        )}
        {campaign && <Box sx={{ mb: 6 }}><DemandPanel campaignId={campaignId} editable={canAssign} /></Box>}
        {error && <Alert severity="error">{(error as Error).message}</Alert>}
        {isPending && <SkeletonCards columns="1fr" />}
        {rows && (
          <Surface sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 1000 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Materials</TableCell>
                  <TableCell>Reviewers</TableCell>
                  <TableCell>Who signs in</TableCell>
                  <TableCell>Submission</TableCell>
                  {canAssign && <TableCell sx={{ width: 88 }}><Box component="span" sx={{ position: 'absolute', left: -9999 }}>Actions</Box></TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={canAssign ? 6 : 5} sx={{ color: 'text.secondary', py: 6 }}>
                      Nothing assigned yet. {canAssign ? 'Add suppliers one at a time, or import a spreadsheet of them (Template gives the columns).' : ''}
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((r) => (
                  <TableRow key={r.supplierId} sx={{ verticalAlign: 'top', '& td': { py: 3 } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CountryFlag code={r.countryCode} size={12} />
                        <Box><Box sx={{ fontWeight: 500, color: 'text.primary' }}>{r.supplier}</Box>{r.parentCompany && <Box sx={{ fontSize: 12, color: 'text.secondary' }}>{r.parentCompany.name}</Box>}</Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {r.materials.map((m) => (
                        <Box key={m.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.25 }}>
                          <Box component="span" sx={{ fontFamily: tokens.font.mono, fontSize: 11, color: 'text.secondary', minWidth: 56 }}>{m.code}</Box>
                          <span>{m.name}</span>
                          {m.ingredient && <Tooltip title="The ingredient you say it is: it counts towards that need, and the supplier confirms it"><Box component="span" sx={{ color: 'text.secondary', fontSize: 12 }}>· {m.ingredient.name}</Box></Tooltip>}
                          <Tooltip title="What you buy of it in this campaign, a year"><Box component="span" sx={{ color: 'text.secondary', fontSize: 12 }}>· {m.annualVolumeT.toLocaleString('en-GB')} t</Box></Tooltip>
                          {m.prefilled && <Tooltip title="Pre-filled from last year's approved answer; the supplier confirms it"><Chip size="small" icon={<History size={11} />} label="Last year" /></Tooltip>}
                        </Box>
                      ))}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ fontSize: 13 }}>{r.reviewers.level1.map((p) => p.fullName ?? p.email).join(', ') || <Box component="span" sx={{ color: tokens.color.warning }}>No buyer</Box>}</Box>
                      <Box sx={{ fontSize: 12, color: 'text.secondary' }}>{r.reviewers.level2.map((p) => p.fullName ?? p.email).join(', ') || <Box component="span" sx={{ color: tokens.color.warning }}>No regional lead</Box>}</Box>
                    </TableCell>
                    <TableCell>
                      {r.logins.length === 0 && <Box component="span" sx={{ color: tokens.color.warning }}>Nobody</Box>}
                      {r.logins.map((l) => (
                        <Box key={l.id} sx={{ fontSize: 13, py: 0.25 }}>
                          <Box>{l.fullName ?? l.email} <Box component="span" sx={{ fontSize: 12, color: l.signedIn ? 'text.secondary' : tokens.color.warning }}>· {l.signedIn ? 'signed in' : 'not yet'}</Box></Box>
                          {l.fullName && l.email && <Box sx={{ fontSize: 12, color: 'text.secondary', wordBreak: 'break-all' }}>{l.email}</Box>}
                          <LastEmail email={l.lastEmail} />
                        </Box>
                      ))}
                    </TableCell>
                    <TableCell>
                      {r.submissionStatus ? (STATUS_LABEL as Record<string, string>)[r.submissionStatus] ?? r.submissionStatus : '—'}
                      {r.lastReminderAt && <Box sx={{ fontSize: 12, color: 'text.secondary' }}>Reminded {formatDate(r.lastReminderAt)}</Box>}
                    </TableCell>
                    {canAssign && (
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Change materials, reviewers or contacts">
                          <IconButton aria-label={`Change ${r.supplier}`} onClick={() => setAssigning({ kind: 'edit', assignment: r })} sx={{ color: tokens.color.ink500 }}>
                            <Pencil size={16} strokeWidth={1.75} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove from this campaign">
                          <IconButton aria-label={`Remove ${r.supplier} from this campaign`} onClick={() => setRemoving(r)} sx={{ color: tokens.color.ink500, '&:hover': { color: tokens.color.danger } }}>
                            <Trash2 size={16} strokeWidth={1.75} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Surface>
        )}
      </Box>
      {importing && <ImportAssignments campaignId={campaignId} onClose={() => setImporting(false)} />}
      {assigning && (
        <AssignmentDialog campaignId={campaignId} campaignOpen={status === 'open'} mode={assigning} onClose={() => setAssigning(null)} onDone={setNotice} />
      )}
      {removing && (
        <DeleteDialog target={{ kind: 'assignment', id: removing.supplierId, campaignId }} onClose={() => setRemoving(null)}
          onDeleted={() => setNotice(`${removing.supplier} was taken out of the campaign.`)} />
      )}
      <Dialog open={confirm !== null} onClose={() => setConfirm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{confirm === 'open' ? 'Open the campaign?' : 'Close the campaign?'}</DialogTitle>
        <DialogContent>
          <Typography>
            {confirm === 'open'
              ? 'Every supplier assigned is emailed a link to the portal now. Suppliers who were never signed in get an invitation.'
              : 'Suppliers can no longer change or submit anything. Reviews of what was submitted carry on. A closed campaign is not reopened.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 6, pb: 5, gap: 2 }}>
          <Button variant="text" onClick={() => setConfirm(null)}>Cancel</Button>
          <Button color={confirm === 'close' ? 'error' : 'primary'} disabled={update.isPending} onClick={() => go(confirm === 'open' ? 'open' : 'closed')}>
            {confirm === 'open' ? 'Open and email suppliers' : 'Close'}
          </Button>
        </DialogActions>
      </Dialog>
      {deleting && (
        <DeleteDialog target={{ kind: 'campaign', id: campaignId }} onClose={() => setDeleting(false)} onDeleted={() => navigate('/sourcing/campaigns')} />
      )}
    </AppShell>
  )
}

function ImportAssignments({ campaignId, onClose }: { campaignId: string; onClose: () => void }) {
  return (
    <ImportDialog
      title="Import assignments"
      run={useImportAssignments(campaignId)}
      summary={(r: AssignmentImport) => [
        `${r.suppliers.added} new suppliers`, `${r.materials.added} new and ${r.materials.changed} changed materials`,
        `${r.assignments.added} new assignments`, `${r.reviewers.added} reviewers added`, `${r.logins.added} supplier logins to invite`,
        r.prefilled ? `${r.prefilled} materials pre-filled from last year` : null,
      ].filter(Boolean).join(' · ')}
      help={<>
        A .csv or .xlsx, one row per supplier material: supplier_ref or supplier_name, supplier_country, parent_company, material_code,
        material_name, material_kind (fixed or blend), commodity_code, annual_volume_t (what you buy of it in this campaign), buyer_email, regional_lead_email,
        supplier_contact_email and supplier_contact_name, and optionally ingredient_code (the ingredient a fixed material is, from the
        taxonomy: it counts towards that need, and the supplier's answer starts from it). Missing suppliers, groups and materials are created; nothing is removed.
        A row may leave the material out to add another contact. Contacts are invited when the campaign opens, or straight away
        if it is open already. “Template” gives the columns.
      </>}
      onClose={onClose}
    />
  )
}

const EMAIL_KIND: Record<string, string> = {
  supplier_campaign_opened: 'Invited',
  supplier_invitation: 'Invited',
  supplier_sign_in: 'Sign-in link',
  supplier_reminder: 'Reminded',
  supplier_returned: 'Returned',
  supplier_approved: 'Approval',
  supplier_help_answered: 'Help answered',
  supplier_proposal_decided: 'Proposal decided',
}

/** The latest email to a supplier login since the campaign began: what it was, whether it went, and when. */
function LastEmail({ email }: { email: { template: string; status: 'pending' | 'sent' | 'failed' | 'bounced' | 'complained'; at: string } | null | undefined }) {
  if (!email) return <Box sx={{ fontSize: 12, color: 'text.disabled' }}>Not emailed yet</Box>
  const kind = EMAIL_KIND[email.template] ?? 'Emailed'
  // A bounce or a spam report means the address will not be mailed again (SES suppresses it): say so plainly.
  if (email.status === 'bounced' || email.status === 'complained') {
    return (
      <Box sx={{ fontSize: 12, color: tokens.color.danger, fontWeight: 500 }}>
        {email.status === 'bounced' ? 'Bounced' : 'Marked as spam'} · {formatDate(email.at)}
        <Box component="span" sx={{ fontWeight: 400 }}> ({kind.toLowerCase()})</Box>
      </Box>
    )
  }
  const state = email.status === 'sent' ? formatDate(email.at) : email.status === 'pending' ? 'sending…' : 'failed to send'
  return (
    <Box sx={{ fontSize: 12, color: email.status === 'failed' ? tokens.color.danger : 'text.secondary' }}>
      {kind} · {state}
    </Box>
  )
}
